import type { DxfText } from '@frontend/api';
import type { Point2 } from '@frontend/viewer-core';

/** World-space centre of a text's oriented box. */
const textCentre = ({ pos, width, height, rotation }: DxfText): Point2 => {
  const theta = (rotation * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  return [
    pos[0] + halfWidth * cos - halfHeight * sin,
    pos[1] + halfWidth * sin + halfHeight * cos,
  ];
};

/**
 * Puts texts in reading order: rows from the top of the drawing down, then left
 * to right within a row.
 *
 * The text layer renders one span per text in this order, and a selection dragged
 * from one label to another takes every span between them in DOM order. File
 * order would sweep in labels nowhere near the drag.
 *
 * Rows are cut greedily rather than by a tolerance comparator, which would not be
 * transitive: a text joins the current row while its centre is within half a line
 * of the row's first text, so a row of mixed-height labels stays together.
 *
 * World orientation only — following the view's quarter turns would mean
 * re-ordering the DOM on every rotate.
 */
export const inReadingOrder = <T extends { text: DxfText }>(
  texts: readonly T[],
): T[] => {
  const placed = texts
    .map((entry) => ({ entry, centre: textCentre(entry.text) }))
    .sort((a, b) => b.centre[1] - a.centre[1]);

  const rows: (typeof placed)[] = [];
  let rowTop = Infinity;
  let rowTolerance = 0;
  placed.forEach((item) => {
    const currentRow = rows[rows.length - 1];
    if (currentRow && rowTop - item.centre[1] <= rowTolerance) {
      currentRow.push(item);
      return;
    }
    rows.push([item]);
    rowTop = item.centre[1];
    rowTolerance = item.entry.text.charHeight / 2;
  });

  return rows.flatMap((row) =>
    row.sort((a, b) => a.centre[0] - b.centre[0]).map(({ entry }) => entry),
  );
};

/**
 * The characters of `label` that `range` covers, or `null` when it covers none.
 *
 * Offsets are measured as text lengths rather than read off the range, because a
 * boundary can sit on the label element itself (offset = child index) as well as
 * inside its text node (offset = character index).
 */
const selectedSlice = (range: Range, label: HTMLElement): string | null => {
  if (!range.intersectsNode(label)) {
    return null;
  }
  const text = label.textContent ?? '';
  const offsetOf = (container: Node, offset: number) => {
    const before = label.ownerDocument.createRange();
    before.setStart(label, 0);
    before.setEnd(container, offset);
    return before.toString().length;
  };
  const start = label.contains(range.startContainer)
    ? offsetOf(range.startContainer, range.startOffset)
    : 0;
  const end = label.contains(range.endContainer)
    ? offsetOf(range.endContainer, range.endOffset)
    : text.length;
  return start < end ? text.slice(start, end) : null;
};

/**
 * What a copy from the DXF text layer should put on the clipboard: the selected
 * part of each visible label, one label per line, in the layer's DOM order.
 *
 * The browser's own serialisation of absolutely positioned, transformed spans
 * inserts line breaks inconsistently between engines — sometimes none, running
 * neighbouring labels together — so the layer builds the text itself.
 *
 * Returns `null`, leaving the copy to the browser, when nothing is selected or
 * when any part of the selection lies outside the layer: a selection that runs on
 * into the page is the page's to serialise. Labels on hidden layers
 * (`display: none`) are skipped, so a copy never carries text the user cannot see.
 */
export const selectedLabelText = (
  layer: HTMLElement,
  selection: Selection | null,
): string | null => {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }
  const ranges = Array.from({ length: selection.rangeCount }, (_, index) =>
    selection.getRangeAt(index),
  );
  if (!ranges.every((range) => layer.contains(range.commonAncestorContainer))) {
    return null;
  }

  const lines: string[] = [];
  Array.from(layer.children).forEach((label) => {
    if (!(label instanceof HTMLElement) || label.style.display === 'none') {
      return;
    }
    ranges.forEach((range) => {
      const slice = selectedSlice(range, label);
      if (slice !== null) {
        lines.push(slice);
      }
    });
  });
  return lines.length > 0 ? lines.join('\n') : null;
};
